import {
  APIComponentAttributes,
  APIComponentType,
  APIEntityType,
  APIFieldAttributes,
  APIReferenceAttributes,
  APIContributor,
  ArdoqId,
  APIReferenceType,
  FEParentChildReferenceAttributes,
  APIWorkspaceAttributes,
  LinkedComponent,
} from '@ardoq/api-types';
import { getAddToMaps, isTruthy, Maybe } from '@ardoq/common-helpers';
import {
  getEmptyComponentGraphData,
  getEmptyGraphModel,
  EnhancedScopeData,
  GraphModelShape,
  ModelTypes,
  SimpleReferenceModel,
} from '@ardoq/data-model';
import { invariant } from '@ardoq/invariant';
import { VIRTUAL_MODEL_ID } from '@ardoq/global-consts';
import { getModelTypeDictionaryKey } from './internal/modelTypesUtils';
import { getWorkspaceModel } from './workspaceUtils';
import { difference } from 'lodash';
import * as colorsAndRepresentations from './colorsAndRepresentations';
import { componentOperations, modelOperations } from '@ardoq/core';

const getEmpty = (): EnhancedScopeData => {
  return {
    calculatedFields: new Set(),
    components: [],
    componentsById: {},
    dateRangeFieldMap: new Map(),
    fieldLabelByFieldName: {},
    fields: [],
    attachments: [],
    fieldsById: {},
    fieldsByModelIdAndTypeId: {},
    graphData: getEmptyGraphModel(),
    hierarchy: new Map(),
    models: [],
    modelsById: {},
    references: [],
    referencesById: {},
    scopeComponentIds: new Set(),
    tags: [],
    tagsById: {},
    typesByModelId: {},
    users: [],
    organizationUsers: [],
    workspaces: [],
    workspacesById: {},
    workspacesByModelId: {},
  };
};

const getModelTypes = (
  enhancedScopeData: EnhancedScopeData,
  modelId: ArdoqId
): ModelTypes => {
  const modelTypes = enhancedScopeData.typesByModelId[modelId];
  invariant(modelTypes, 'Missing model types in enhancedScopeData', {
    modelId,
  });

  return modelTypes;
};

const getComponentTypeByComponentId = (
  enhancedScopeData: EnhancedScopeData,
  componentId: string
): APIComponentType => {
  const component = enhancedScopeData.componentsById[componentId];
  invariant(component, 'Missing component in enhancedScopeData', {
    componentId,
  });

  const modelTypes = getModelTypes(enhancedScopeData, component.model);
  return modelTypes.componentTypesById[component.typeId];
};

const getAPIComponentTypesByWorkspaceId = (
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId
): APIComponentType[] => {
  const model = getWorkspaceModel(scopeData, workspaceId);
  if (!model) {
    return [];
  }
  return Object.values(scopeData.typesByModelId[model._id].componentTypesById);
};

const getAPIComponentTypesWithParentsByWorkspaceId = (
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId
): APIComponentType[] => {
  const modelId = getWorkspaceModel(scopeData, workspaceId);
  const model = modelId;
  if (!model) {
    return [];
  }
  const typesWithParent = modelOperations.getComponentTypesWithParents(model);
  return Object.values(typesWithParent.componentTypesById);
};

const getAllAPIComponentTypes = (
  scopeData: EnhancedScopeData
): APIComponentType[] => {
  return Object.values(scopeData.typesByModelId).flatMap(modelTypes =>
    Object.values(modelTypes.componentTypesById)
  );
};

const getModelReferenceTypes = (
  enhancedScopeData: EnhancedScopeData,
  modelId: ArdoqId
): APIReferenceType[] => {
  const modelTypes = getModelTypes(enhancedScopeData, modelId);
  // We map through the referenceTypesById to
  // preserve the order of the reference types
  return modelTypes.referenceTypes.map(
    refTypeId => modelTypes.referenceTypesById[refTypeId]
  );
};

const getReference = (
  enhancedScopeData: EnhancedScopeData,
  referenceId: string
): APIReferenceAttributes | undefined => {
  return enhancedScopeData.referencesById[referenceId];
};

const getComponent = (
  enhancedScopeData: EnhancedScopeData,
  componentId: string
): APIComponentAttributes | undefined => {
  return enhancedScopeData.componentsById[componentId];
};

const getComponentFields = (
  enhancedScopeData: EnhancedScopeData,
  componentId: ArdoqId
): Record<string, APIFieldAttributes> => {
  const component = getComponent(enhancedScopeData, componentId);
  if (!component) {
    return {};
  }

  const key = getModelTypeDictionaryKey(component.model, component.typeId);
  const fields = enhancedScopeData.fieldsByModelIdAndTypeId[key] || {};
  return fields;
};

const getComponentFieldNames = (
  enhancedScopeData: EnhancedScopeData,
  componentId: ArdoqId
): string[] => Object.keys(getComponentFields(enhancedScopeData, componentId));

const getReferenceFields = (
  enhancedScopeData: EnhancedScopeData,
  referenceId: ArdoqId
): Record<string, APIFieldAttributes> => {
  const reference = getReference(enhancedScopeData, referenceId);
  if (!reference) {
    return {};
  }

  const typeId = String(reference.type);
  const key = getModelTypeDictionaryKey(reference.model, typeId);
  const fields = enhancedScopeData.fieldsByModelIdAndTypeId[key] || {};
  return fields;
};

const getReferenceFieldNames = (
  enhancedScopeData: EnhancedScopeData,
  referenceId: ArdoqId
): string[] => Object.keys(getReferenceFields(enhancedScopeData, referenceId));

const isScopeComponent = (
  enhancedScopeData: EnhancedScopeData,
  componentId: ArdoqId
) => enhancedScopeData.scopeComponentIds.has(componentId);

const isComponent = (
  enhancedScopeData: EnhancedScopeData,
  componentId: ArdoqId
): boolean => {
  return isTruthy(enhancedScopeData.componentsById[componentId]);
};

const countIncomingReferences = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
): number => {
  return scopeData.graphData.targetMap.get(componentId)?.length || 0;
};

const countOutgoingReferences = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
): number => {
  return scopeData.graphData.sourceMap.get(componentId)?.length || 0;
};

const isReference = (reference?: APIReferenceAttributes): boolean =>
  reference?.ardoq?.['entity-type'] === APIEntityType.REFERENCE;

const getUser = (
  scopeData: EnhancedScopeData,
  userId: ArdoqId
): Maybe<APIContributor> =>
  scopeData.users.find(user => user._id === userId) ?? null;

const getOrganizationUser = (scopeData: EnhancedScopeData, userId: ArdoqId) =>
  scopeData.organizationUsers.find(user => user._id === userId) ?? null;

const getFieldByName = (
  scopeData: EnhancedScopeData,
  fieldName: string
): Maybe<APIFieldAttributes> =>
  scopeData.fields.find(field => field.name === fieldName) ?? null;

/* Returns all fields that matches the name in the scope data. There might be multiple from different models*/
const getFieldsByName = (
  scopeData: EnhancedScopeData,
  fieldName: string
): APIFieldAttributes[] =>
  scopeData.fields.filter(field => field.name === fieldName);

const getField = (
  scopeData: EnhancedScopeData,
  fieldId: ArdoqId
): Maybe<APIFieldAttributes> => scopeData.fieldsById[fieldId] ?? null;

const getComponentTypeByModelIdAndTypeId = (
  scopeData: EnhancedScopeData,
  modelId: ArdoqId,
  typeId: ArdoqId
) => scopeData.typesByModelId[modelId]?.componentTypesById[typeId] ?? null;

const getReferenceTypeByModelIdAndTypeId = (
  scopeData: EnhancedScopeData,
  modelId: ArdoqId,
  typeId: number
): Maybe<APIReferenceType> =>
  scopeData.typesByModelId[modelId]?.referenceTypesById[String(typeId)] ?? null;

const getModelIdByWorkspaceId = (
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId
): ArdoqId | null => {
  return scopeData.workspacesById[workspaceId]?.componentModel;
};

const getWorkspace = (
  enhancedScopeData: EnhancedScopeData,
  workspaceId: string
): APIWorkspaceAttributes | undefined => {
  return enhancedScopeData.workspacesById[workspaceId];
};

const getWorkspaceIdByComponentModelId = (
  scopeData: EnhancedScopeData,
  modelId: ArdoqId
): ArdoqId | null => {
  return scopeData.workspacesByModelId[modelId]?._id ?? null;
};

const getComponentTypeIdsByWorkspaceId = (
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId
): string[] => {
  const componentModelId = getModelIdByWorkspaceId(scopeData, workspaceId);
  return componentModelId
    ? (scopeData.typesByModelId[componentModelId]?.componentTypes ?? [])
    : [];
};

const isRigidModel = (scopeData: EnhancedScopeData, modelId: ArdoqId) =>
  !scopeData.modelsById[modelId]?.flexible;

const getGraphSnapshot = (
  scopeData: EnhancedScopeData,
  parentChildReferences: FEParentChildReferenceAttributes[],
  scopeReferenceIds: ArdoqId[]
): GraphModelShape => {
  const visibleReferenceIds = new Set(scopeReferenceIds);
  const referencesInTheGraph = scopeData.references.filter(reference =>
    visibleReferenceIds.has(reference._id)
  );
  const sourceMap = new Map<ArdoqId, LinkedComponent[]>();
  const targetMap = new Map<ArdoqId, LinkedComponent[]>();
  const referenceMap = new Map<ArdoqId, SimpleReferenceModel>();
  const addToMaps = getAddToMaps({ referenceMap, sourceMap, targetMap });
  referencesInTheGraph.forEach(
    ({ _id: referenceId, source: sourceId, target: targetId }) =>
      addToMaps({ referenceId, sourceId, targetId })
  );
  parentChildReferences.forEach(({ source, target, _id }) =>
    addToMaps({ referenceId: _id, sourceId: source, targetId: target })
  );
  return {
    ...getEmptyComponentGraphData(),
    sourceMap,
    targetMap,
    referenceMap,
    referenceTypes: [],
  };
};

const getTagEntityCollectionName = (
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE
) => (entityType === APIEntityType.REFERENCE ? 'references' : 'components');

const getRootWorkspaceIdFromEntityId = (
  entityId: ArdoqId,
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  scopeData: EnhancedScopeData
): string | null => {
  if (entityType === APIEntityType.COMPONENT) {
    return scopeData.componentsById[entityId]?.rootWorkspace;
  }
  if (entityType === APIEntityType.REFERENCE) {
    return scopeData.referencesById[entityId]?.rootWorkspace;
  }
  return null;
};

const getTagsToAdd = (
  entityId: ArdoqId,
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  previousScopeData: EnhancedScopeData,
  nextScopeData: EnhancedScopeData
): { entityId: ArdoqId; tagNames: string[]; workspaceId: string | null } => {
  const entityKey = getTagEntityCollectionName(entityType);
  const workspaceId = getRootWorkspaceIdFromEntityId(
    entityId,
    entityType,
    nextScopeData
  );

  const previousTags = previousScopeData.tags
    .filter(tag => tag[entityKey].includes(entityId))
    .map(tag => tag.name);
  const nextTags = nextScopeData.tags
    .filter(tag => tag[entityKey].includes(entityId))
    .map(tag => tag.name);

  const diffTags = difference(nextTags, previousTags);
  return { entityId, tagNames: diffTags, workspaceId };
};

const getTagsToRemove = (
  entityId: ArdoqId,
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  previousScopeData: EnhancedScopeData,
  nextScopeData: EnhancedScopeData
): { entityId: ArdoqId; tagNames: string[] } => {
  const entityKey = getTagEntityCollectionName(entityType);

  const previousTags = previousScopeData.tags
    .filter(tag => tag[entityKey].includes(entityId))
    .map(tag => tag.name);
  const nextTags = nextScopeData.tags
    .filter(tag => tag[entityKey].includes(entityId))
    .map(tag => tag.name);
  const diffTags = difference(previousTags, nextTags);
  return { entityId, tagNames: diffTags };
};

const getComponentTypesByComponentTypeName = (
  scopeData: EnhancedScopeData,
  componentTypeName: string
): APIComponentType[] => {
  return Object.values(scopeData.typesByModelId).flatMap(modelTypes =>
    Object.values(modelTypes.componentTypesById).filter(componentType => {
      return componentType.name === componentTypeName;
    })
  );
};

const getReferenceTypeByReferenceId = (
  scopeData: EnhancedScopeData,
  referenceId: ArdoqId
): APIReferenceType => {
  const reference = getReference(scopeData, referenceId);
  invariant(
    reference,
    `Reference with id ${referenceId} does not exist in scopeData`
  );

  const model = scopeData.modelsById[reference.model];
  const referenceType = model.referenceTypes[String(reference.type)];

  return referenceType ?? null;
};

/**
 * Resolve a reference's type from its `model` + `type` (works for both real and
 * virtual references, since the virtual model is part of `modelsById`). Unlike
 * {@link getReferenceTypeByReferenceId} this is null-safe and does not require
 * the reference to be in the scope data, so it is safe to call from the model
 * interfaces with arbitrary reference attributes.
 */
const getReferenceType = (
  scopeData: EnhancedScopeData,
  reference: APIReferenceAttributes
): APIReferenceType | null =>
  scopeData.modelsById[reference.model]?.referenceTypes[
    String(reference.type)
  ] ?? null;

/**
 * Virtual (collapsed-path) reference types grouped by the workspace they appear
 * in, keyed by reference type id — the scope-data equivalent of the backbone
 * `workspaceInterface.getVirtualReferenceTypes`. Derived from the virtual
 * references in the scope data (each carries its `rootWorkspace`) and the
 * synthetic virtual model.
 */
const getVirtualReferenceTypesByWorkspace = (
  scopeData: EnhancedScopeData
): Record<ArdoqId, Record<string, APIReferenceType>> => {
  const virtualModel = scopeData.modelsById[VIRTUAL_MODEL_ID];
  if (!virtualModel) return {};
  const result: Record<ArdoqId, Record<string, APIReferenceType>> = {};
  for (const reference of scopeData.references) {
    if (reference.model !== VIRTUAL_MODEL_ID) continue;
    const typeId = String(reference.type);
    const referenceType = virtualModel.referenceTypes[typeId];
    if (!referenceType) continue;
    (result[reference.rootWorkspace] ??= {})[typeId] = referenceType;
  }
  return result;
};

const getReferenceTypeIdBySourceModelIdAndReferenceTypeName = (
  scopeData: EnhancedScopeData,
  sourceComponentModelId: string,
  referenceTypeName: string
): number | undefined => {
  return Object.values(
    scopeData.modelsById[sourceComponentModelId]?.referenceTypes ?? {}
  ).find(referenceType => referenceType.name === referenceTypeName)?.id;
};

const getReferenceTypeIdBySourceComponentId = (
  scopeData: EnhancedScopeData,
  sourceComponentId: string,
  referenceTypeName: string
): number | undefined => {
  const sourceModelId = scopeData.componentsById[sourceComponentId]?.model;
  if (!sourceModelId) {
    return undefined;
  }
  return Object.values(
    scopeData.modelsById[sourceModelId]?.referenceTypes ?? {}
  ).find(referenceType => referenceType.name === referenceTypeName)?.id;
};

const referenceTypeExistsInModel = (
  scopeData: EnhancedScopeData,
  modelId: ArdoqId,
  referenceTypeName: string
) => {
  const sourceModel = scopeData.models.find(model => model._id === modelId);
  return sourceModel
    ? Object.values(sourceModel.referenceTypes).some(
        refType => refType.name === referenceTypeName
      )
    : false;
};

const getComponentColor = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
) => {
  const type = getComponentTypeByComponentId(scopeData, componentId);
  return colorsAndRepresentations.getComponentColor(
    scopeData,
    componentId,
    type
  );
};

const getColor = (
  scopeData: EnhancedScopeData,
  entityType: APIEntityType,
  entityId: ArdoqId | number,
  parentEntityId?: ArdoqId
) => {
  switch (entityType) {
    case APIEntityType.COMPONENT:
      return getComponentColor(scopeData, entityId as string);
    case APIEntityType.COMPONENT_TYPE:
      if (!parentEntityId) return colorsAndRepresentations.DEFAULT_COLOR;

      return colorsAndRepresentations.getComponentTypeColor(
        scopeData,
        entityId as string,
        parentEntityId
      );
  }
  return colorsAndRepresentations.DEFAULT_COLOR;
};

const getComponentRepresentationDataWithColor = (
  scopeData: EnhancedScopeData,
  componentId: string
) => {
  const componentRepresentationData =
    colorsAndRepresentations.getComponentRepresentationData(
      scopeData,
      componentId
    );
  const color = getComponentColor(scopeData, componentId);
  return {
    ...componentRepresentationData,
    color,
  };
};

const getChildrenIds = (
  enhancedScopeData: EnhancedScopeData,
  parentComponentId: ArdoqId
): ArdoqId[] => {
  const { rootWorkspace } = enhancedScopeData.componentsById[parentComponentId];
  const workspaceHierarchy =
    enhancedScopeData.hierarchy.get(rootWorkspace) ||
    new Map<ArdoqId, ArdoqId[]>();
  return workspaceHierarchy.get(parentComponentId) || [];
};

const getDescendantComponentIds = (
  enhancedScopeData: EnhancedScopeData,
  componentIds: ArdoqId[]
): ArdoqId[] => {
  const descendants = componentIds.flatMap(componentId =>
    collectDescendants(enhancedScopeData, componentId)
  );
  return [...new Set(descendants)];
};

const collectDescendants = (
  enhancedScopeData: EnhancedScopeData,
  currentComponentId: ArdoqId,
  descendants: ArdoqId[] = []
) => {
  const childrenIds = getChildrenIds(enhancedScopeData, currentComponentId);
  for (const childId of childrenIds) {
    descendants.push(childId);
    collectDescendants(enhancedScopeData, childId, descendants);
  }
  return descendants;
};

const getParentId = (
  enhancedScopeData: EnhancedScopeData,
  componentId: ArdoqId
): ArdoqId | null => {
  const component = getComponent(enhancedScopeData, componentId);
  if (!component) {
    // TODO handle error case
    return null;
  }
  return component.parent || null;
};

const getAncestorIds = (
  enhancedScopeData: EnhancedScopeData,
  componentId: ArdoqId,
  ancestorIds: ArdoqId[] = []
): ArdoqId[] => {
  const parentId = getParentId(enhancedScopeData, componentId);
  if (parentId) {
    ancestorIds.unshift(parentId);
    return getAncestorIds(enhancedScopeData, parentId, ancestorIds);
  }
  return ancestorIds;
};

const getComponentWorkspaceId = (
  enhancedScopeData: EnhancedScopeData,
  componentId: ArdoqId
): ArdoqId | null => {
  const component = getComponent(enhancedScopeData, componentId);
  if (!component) {
    return null;
  }
  return component.rootWorkspace || null;
};

const getFileName = (
  enhancedScopeData: EnhancedScopeData,
  fileId: string
): string | null => {
  const attachment = enhancedScopeData.attachments?.find(a => a._id === fileId);

  return attachment?.filename ?? null;
};

const getPossibleParents = (
  enhancedScopeData: EnhancedScopeData,
  componentId: ArdoqId
): APIComponentAttributes[] => {
  const component = enhancedScopeData.componentsById[componentId];
  if (!component) return [];

  const workspaceComponents = enhancedScopeData.components.filter(
    c => c.rootWorkspace === component.rootWorkspace
  );
  const descendantIds = getDescendantComponentIds(enhancedScopeData, [
    componentId,
  ]);
  const isFlexible =
    enhancedScopeData.modelsById[component.model]?.flexible ?? false;
  const componentTypesById =
    enhancedScopeData.typesByModelId[component.model]?.componentTypesById ?? {};

  return componentOperations.getPossibleParents(
    component,
    workspaceComponents,
    descendantIds,
    isFlexible,
    componentTypesById
  );
};

export const enhancedScopeDataOperations = {
  countIncomingReferences,
  countOutgoingReferences,
  isScopeComponent,
  isComponent,
  getEmpty,
  getComponentTypeByComponentId,
  getReferenceTypeByReferenceId,
  getReferenceType,
  getVirtualReferenceTypesByWorkspace,
  getReferenceTypeIdBySourceModelIdAndReferenceTypeName,
  getReferenceTypeIdBySourceComponentId,
  referenceTypeExistsInModel,
  getReference,
  getComponent,
  getComponentFields,
  getComponentFieldNames,
  getReferenceFields,
  getReferenceFieldNames,
  isReference,
  getUser,
  getOrganizationUser,
  getFieldByName,
  getFieldsByName,
  getField,
  getComponentTypeByModelIdAndTypeId,
  getReferenceTypeByModelIdAndTypeId,
  isRigidModel,
  getModelReferenceTypes,
  getWorkspace,
  getModelIdByWorkspaceId,
  getWorkspaceIdByComponentModelId,
  getComponentTypeIdsByWorkspaceId,
  getAPIComponentTypesWithParentsByWorkspaceId,
  getAPIComponentTypesByWorkspaceId,
  getAllAPIComponentTypes,
  getGraphSnapshot,
  getTagsToAdd,
  getTagsToRemove,
  getComponentTypesByComponentTypeName,
  getComponentColor,
  getColor,
  getComponentRepresentationDataWithColor,
  getComponentTypeColor: colorsAndRepresentations.getComponentTypeColor,
  getComponentRepresentationData:
    colorsAndRepresentations.getComponentRepresentationData,
  getComponentTypeRepresentationData:
    colorsAndRepresentations.getComponentTypeRepresentationData,
  getFallbackComponentRepresentation:
    colorsAndRepresentations.getFallbackComponentRepresentation,
  getComponentTypeRepresentationDataWithColorShades:
    colorsAndRepresentations.getComponentTypeRepresentationDataWithColorShades,
  getComponentRepresentationDataWithColorShades:
    colorsAndRepresentations.getComponentRepresentationDataWithColorShades,
  getComponentRepresentationDataWithColorShadesFallback:
    colorsAndRepresentations.getComponentRepresentationDataWithColorShadesFallback,
  getDescendantComponentIds,
  getChildrenIds,
  getParentId,
  getAncestorIds,
  getComponentWorkspaceId,
  getFileName,
  getPossibleParents,
};
